'use client';

/**
 * SetupChecklistTopbarTrigger — icon button în top-bar care deschide checklist-ul
 * într-un dropdown popover. Înlocuiește vechiul floating button bottom-right.
 *
 * Vizibil:
 *   - Mereu (în top-bar) cât timp checklist-ul NU e finalizat și marcat seen
 *   - Hide permanent după allComplete && completedAt
 *
 * Click toggle dropdown cu:
 *   - Header progress (count + percent + bar)
 *   - Pending items (clickable → router.push)
 *   - "Vezi pe dashboard" link
 */

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ListChecks, ChevronRight, Check, Circle, X, Sparkles,
} from 'lucide-react';
import {
  useSetupStatus, useDismissChecklist, useReopenChecklist,
} from '@/features/onboarding/hooks/use-setup-status';
import type { ChecklistItem } from '@/features/onboarding/services/setup-status-api';

export function SetupChecklistTopbarTrigger() {
  const { data } = useSetupStatus();
  const reopen = useReopenChecklist();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Hide complet când checklist-ul e finalizat și user a văzut banner-ul de reward
  if (!data) return null;
  if (data.allComplete && data.completedAt) return null;

  const allComplete = data.allComplete;
  const pendingItems = data.items.filter((i) => !i.done && i.group !== 'optional');
  const optionalPending = data.items.filter((i) => !i.done && i.group === 'optional');

  const handleItemClick = (item: ChecklistItem) => {
    setOpen(false);
    router.push(item.cta.href);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => {
          // Dacă widget-ul e dismissed, redeschide-l implicit la prima interacțiune
          if (data.dismissedAt) reopen.mutate();
          setOpen((o) => !o);
        }}
        title={`Configurarea contului · ${data.completedCount}/${data.totalItems}`}
        className={
          'relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors ' +
          (allComplete
            ? 'text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
            : 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20')
        }
      >
        <ListChecks className="h-5 w-5" />
        {/* Progress badge — număr mic în colțul din dreapta-sus al icon-ului */}
        <span
          className={
            'absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[0.625rem] font-bold flex items-center justify-center ring-2 ring-white dark:ring-neutral-950 tabular-nums ' +
            (allComplete
              ? 'bg-emerald-500 text-white'
              : 'bg-amber-500 text-white')
          }
        >
          {allComplete ? '✓' : `${data.percentComplete}%`}
        </span>
      </button>

      {/* Dropdown popover */}
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] z-50 w-[400px] max-h-[80vh] overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="relative flex items-center gap-3 px-4 py-3.5 border-b border-neutral-100 dark:border-neutral-800/80">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-amber-50 dark:bg-amber-950/40 ring-1 ring-amber-200/70 dark:ring-amber-800/60">
              <ListChecks className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[0.8125rem] font-semibold text-neutral-900 dark:text-neutral-100">
                Configurarea contului
              </div>
              <div className="text-[0.6875rem] text-neutral-500 dark:text-neutral-400 mt-0.5 tabular-nums">
                {data.completedCount} din {data.totalItems} sarcini · {data.percentComplete}% complet
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="grid h-7 w-7 place-items-center rounded-md text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Slim progress bar */}
          <div className="relative h-[3px] mx-4 mt-2 mb-0 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-500 via-orange-500 to-emerald-500 transition-[width] duration-500 ease-out rounded-full"
              style={{ width: `${data.percentComplete}%` }}
            />
          </div>

          {/* Reward state — toate complete dar încă nu seen */}
          {allComplete && (
            <div className="px-4 py-6 text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-3 py-1.5 mb-3">
                <Sparkles className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">100% complet</span>
              </div>
              <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Felicitări! Contul tău este complet configurat
              </h4>
              <button
                onClick={() => { setOpen(false); router.push('/'); }}
                className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white"
              >
                Vezi pe dashboard
              </button>
            </div>
          )}

          {/* Pending items list */}
          {!allComplete && (
            <div className="overflow-y-auto px-4 py-3 space-y-1.5">
              {pendingItems.length === 0 && optionalPending.length === 0 ? (
                <div className="py-4 text-center text-xs text-neutral-500">
                  Toate task-urile esențiale sunt completate.
                </div>
              ) : null}

              {pendingItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className="group w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg bg-amber-50/70 dark:bg-amber-950/25 ring-1 ring-inset ring-amber-200 dark:ring-amber-800/60 hover:ring-amber-500 dark:hover:ring-amber-500/80 hover:bg-amber-100/60 dark:hover:bg-amber-950/40 transition-all"
                >
                  <div className="grid h-4 w-4 place-items-center rounded-full bg-amber-500/15 ring-1 ring-amber-400/50 shrink-0">
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-500 dark:bg-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[0.8125rem] font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                      {item.label}
                    </div>
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 text-amber-700 dark:text-amber-400 group-hover:translate-x-0.5 transition-transform shrink-0" />
                </button>
              ))}

              {optionalPending.length > 0 && (
                <details className="group mt-2">
                  <summary className="flex items-center gap-2 cursor-pointer list-none select-none py-1.5">
                    <ChevronRight className="h-3.5 w-3.5 text-neutral-400 group-open:rotate-90 transition-transform" />
                    <span className="text-[0.6563rem] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                      Opțional ({optionalPending.length})
                    </span>
                  </summary>
                  <div className="mt-1 space-y-1 pl-2">
                    {optionalPending.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        className="group/o w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg bg-neutral-50 dark:bg-neutral-900/40 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 hover:ring-neutral-400 transition-all"
                      >
                        <Circle className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[0.7813rem] font-medium text-neutral-700 dark:text-neutral-300 truncate">{item.label}</div>
                        </div>
                        <ChevronRight className="h-3 w-3 text-neutral-400 group-hover/o:translate-x-0.5 transition-transform shrink-0" />
                      </button>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* Footer — link to dashboard */}
          {!allComplete && (
            <div className="px-4 py-2 border-t border-neutral-100 dark:border-neutral-800/80 bg-neutral-50/40 dark:bg-neutral-900/40">
              <button
                onClick={() => { setOpen(false); router.push('/'); }}
                className="text-[0.6875rem] text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
              >
                Vezi detalii pe dashboard →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
